import { Link, useLocation, useNavigate } from "react-router-dom";
import { Activity, ArrowLeft, Coins, LayoutDashboard, LogOut, MessagesSquare, Moon, MonitorSmartphone, Plug, ShieldCheck, SlidersHorizontal, Sun, Ticket, Users, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/components/roleNav";
import { useTheme } from "@/context/ThemeContext";

const ICONS = {
  dashboard: LayoutDashboard,
  users: Users,
  codes: Ticket,
  plug: Plug,
  assets: Coins,
  wallet: Wallet,
  markets: SlidersHorizontal,
  shield: ShieldCheck,
  chat: MessagesSquare,
  demo: MonitorSmartphone,
};

export const AppShell = ({ nav, children, title, subtitle }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const HOME = { admin: "/admin", supervisor: "/manager", manager: "/manager", agent: "/manager", user: "/dashboard" };
  const homeLink = HOME[user?.role] || "/dashboard";

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden border-r border-[#22252F] bg-[#0E1015] p-6 lg:flex lg:flex-col lg:justify-between">
        <div className="space-y-10">
          <Link to={homeLink} className="flex items-center gap-3" data-testid="sidebar-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#2563EB]/50 bg-[#2563EB]/10">
              <Activity className="h-4 w-4 text-[#60A5FA]" />
            </span>
            <span className="font-heading text-sm font-semibold uppercase tracking-[0.3em]">Meridian</span>
          </Link>

          <nav className="space-y-1">
            {homeLink && pathname !== homeLink ? (
              <Link
                to={homeLink}
                data-testid="back-to-dashboard"
                className="mb-3 flex items-center gap-3 rounded-md border border-[#2563EB]/40 bg-[#2563EB]/10 px-4 py-3 text-sm text-white transition-colors hover:bg-[#2563EB]/20"
              >
                <ArrowLeft className="h-4 w-4" /> Voltar ao Dashboard
              </Link>
            ) : null}
            {nav.map((item) => {
              const Icon = ICONS[item.icon] || LayoutDashboard;
              const active = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm transition-colors ${
                    active
                      ? "border border-[#2563EB]/40 bg-[#2563EB]/10 text-white"
                      : "border border-transparent text-[#94A3B8] hover:bg-[#151821] hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="space-y-4 border-t border-[#22252F] pt-6">
          <button onClick={toggle} data-testid="theme-toggle"
                  className="flex w-full items-center gap-3 rounded-md border border-[#22252F] px-4 py-3 text-sm text-[#94A3B8] transition-colors hover:text-white">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {theme === "dark" ? "Modo claro" : "Modo escuro"}
          </button>
          <div>
            <p className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">
              {user?.name}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#60A5FA]" data-testid="sidebar-user-role">
              {ROLE_LABEL[user?.role] || user?.role}
            </p>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="flex w-full items-center gap-3 rounded-md border border-[#22252F] px-4 py-3 text-sm text-[#94A3B8] transition-colors hover:border-[#EF4444]/40 hover:text-[#FCA5A5]"
          >
            <LogOut className="h-4 w-4" /> Terminar sessão
          </button>
        </div>
      </aside>

      <main className="min-w-0">
        <header className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-4 border-b border-[#22252F] bg-[#12141A]/80 px-6 py-5 backdrop-blur-xl sm:px-10">
          <div>
            <h1 className="font-heading text-2xl font-light tracking-tight">{title}</h1>
            {subtitle ? <p className="text-sm text-[#94A3B8]">{subtitle}</p> : null}
          </div>
          <div className="flex items-center gap-3 lg:hidden">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`${item.testId}-mobile`}
                className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8]"
              >
                {item.label}
              </Link>
            ))}
            <button
              onClick={doLogout}
              data-testid="logout-button-mobile"
              className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8]"
            >
              Sair
            </button>
          </div>
        </header>
        <div className="px-6 py-8 sm:px-10 sm:py-10">{children}</div>
      </main>
    </div>
  );
};
